import React, { useState, useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import Login from "./components/Login";
import StudentProfile from "./components/StudentProfile";
import SittingArrangement from "./components/SittingArrangement";
import SeatChangeRequest from "./components/SeatChangeRequest";
import PaymentHistory from "./components/PaymentHistory";
import Toast from "./components/Toast";
import "./styles/App.css";

type ActiveView =
  | "profile"
  | "sitting-arrangement"
  | "seat-change-request"
  | "payment-history"
  | "get-receipt";

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const [activeView, setActiveView] = useState<ActiveView>("profile");
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Initialize sidebar state responsively
  useEffect(() => {
    const applyResponsiveSidebar = () => {
      const isMobile = window.innerWidth <= 768;
      setSidebarOpen(!isMobile);
    };
    applyResponsiveSidebar();
    window.addEventListener("resize", applyResponsiveSidebar);
    return () => window.removeEventListener("resize", applyResponsiveSidebar);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [userMenuOpen]);

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  const handleViewChange = (view: ActiveView) => {
    setActiveView(view);
    // Close sidebar on mobile after selecting an option
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const renderContent = () => {
    switch (activeView) {
      case "profile":
        return <StudentProfile />;
      case "sitting-arrangement":
        return <SittingArrangement />;
      case "seat-change-request":
        return <SeatChangeRequest />;
      case "payment-history":
        return <PaymentHistory />;
      default:
        return <StudentProfile />;
    }
  };

  const getViewTitle = () => {
    switch (activeView) {
      case "profile":
        return "Student Profile";
      case "sitting-arrangement":
        return "Sitting Arrangement";
      case "seat-change-request":
        return "Request for Change Seat";
      case "payment-history":
        return "Payment History";
      default:
        return "Student Portal";
    }
  };

  return (
    <div className="App">
      <Toast />
      <div className="topbar">
        <button
          className="sidebar-toggle mobile-only"
          aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 6H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M3 12H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M3 18H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className="topbar-title">{getViewTitle()}</div>
        <div className="topbar-user" ref={userMenuRef}>
          <div className="avatar" aria-hidden="true">
            {user?.avatar || user?.name?.charAt(0).toUpperCase() || "U"}
          </div>
          <div className="user-meta">
            <div className="user-name">{user?.name || "User"}</div>
            <div className="user-role">Student</div>
          </div>
          <button
            className="user-menu"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            aria-label="Open user menu"
            title="User menu"
            onClick={() => setUserMenuOpen((v) => !v)}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {userMenuOpen && (
            <div className="user-dropdown" role="menu">
              <div className="dropdown-user-info">
                <div className="dropdown-user-name">{user?.name}</div>
                <div className="dropdown-user-email">{user?.email}</div>
              </div>
              <div className="dropdown-sep" />
              <button
                role="menuitem"
                className="dropdown-item"
                onClick={() => {
                  setActiveView("profile");
                  setUserMenuOpen(false);
                }}
              >
                Profile
              </button>
              <div className="dropdown-sep" />
              <button
                role="menuitem"
                className="dropdown-item danger"
                onClick={logout}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="layout">
        <aside className={`sidebar ${sidebarOpen ? "open" : "collapsed"}`}>
          <div className="sidebar-header">
            <div className="sidebar-title">
              <span className="sidebar-title-text">Student Portal</span>
            </div>
            <button
              className="sidebar-toggle"
              aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
              title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
          <nav className="sidebar-nav">
            <button
              className={`nav-item ${activeView === "profile" ? "active" : ""}`}
              onClick={() => handleViewChange("profile")}
            >
              <span className="nav-icon">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="7"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="nav-label">Profile</span>
            </button>

            <button
              className={`nav-item ${
                activeView === "sitting-arrangement" ? "active" : ""
              }`}
              onClick={() => handleViewChange("sitting-arrangement")}
            >
              <span className="nav-icon">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 10H20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M6 14H18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <rect
                    x="3"
                    y="4"
                    width="18"
                    height="16"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </span>
              <span className="nav-label">Sitting Arrangement</span>
            </button>

            <button
              className={`nav-item ${
                activeView === "seat-change-request" ? "active" : ""
              }`}
              onClick={() => handleViewChange("seat-change-request")}
            >
              <span className="nav-icon">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L2 7L12 12L22 7L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 17L12 22L22 17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 12L12 17L22 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="nav-label">Request for Change Seat</span>
            </button>

            <button
              className={`nav-item ${
                activeView === "payment-history" ? "active" : ""
              }`}
              onClick={() => handleViewChange("payment-history")}
            >
              <span className="nav-icon">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L2 7L12 12L22 7L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 17L12 22L22 17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 12L12 17L22 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="nav-label">Payment History</span>
            </button>
          </nav>
        </aside>
        <div
          className={`sidebar-backdrop ${sidebarOpen ? "show" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />
        <main className="content with-topbar">{renderContent()}</main>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
